import { FiBook } from 'react-icons/fi';

export const awlSublist1Data = {
  courseId: 'awl-sublist-1',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublist 1',
  icon: FiBook,
  overview: {
    impact: 'Master the most frequently used academic vocabulary for Band 6.5+',
    focus: '12 high-frequency words from Coxhead\'s AWL Sublist 1',
    uses: 'Reading, Writing Task 2',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word Meanings and Usage',
      content: '<p>Sublist 1 contains the most frequent academic words. These appear across all disciplines and are essential for IELTS Reading and Writing.</p>',
      points: [
        'These 12 words appear in over 80% of academic texts',
        'Learning them gives you an immediate boost in reading comprehension',
        'Using them correctly in essays signals academic fluency to examiners',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['analysis', 'A detailed examination of something', 'A careful analysis of the data reveals several trends.'],
          ['approach', 'A way of dealing with something', 'The government adopted a new approach to healthcare.'],
          ['area', 'A particular subject or range of activity', 'Climate change is a key area of scientific research.'],
          ['assessment', 'The act of judging or evaluating', 'Continuous assessment helps track student progress.'],
          ['benefit', 'An advantage or positive result', 'One major benefit of exercise is improved mental health.'],
          ['concept', 'An abstract idea or general notion', 'The concept of sustainability has gained widespread attention.'],
          ['consistent', 'Unchanging; in agreement with', 'The results were consistent across all three experiments.'],
          ['context', 'The circumstances that form the setting', 'It is important to understand vocabulary in context.'],
          ['data', 'Facts and statistics collected for analysis', 'The data suggest a strong correlation between income and education.'],
          ['definition', 'A statement of the exact meaning', 'The legal definition of the term differs from everyday usage.'],
          ['environment', 'The natural world or surrounding conditions', 'Urban environments present unique challenges for wildlife.'],
          ['established', 'Recognised and accepted; set up on a permanent basis', 'The company was established in 1985 and has grown steadily.'],
        ],
      },
    },
    {
      title: 'Word Families',
      content: '<p>Knowing different forms of a word allows you to use it flexibly in Writing and Speaking. IELTS examiners award higher scores for accurate word-form usage.</p>',
      points: [
        'Nouns, verbs, and adjectives often share the same root',
        'Using the correct form avoids grammatical errors in Writing Task 2',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['analysis', 'analysis, analyst', 'analyse', 'analytical, analytically'],
          ['approach', 'approach', 'approach', 'approachable'],
          ['assess', 'assessment, assessor', 'assess', 'assessable'],
          ['benefit', 'benefit, beneficiary', 'benefit', 'beneficial, beneficially'],
          ['concept', 'concept, conception', '—', 'conceptual, conceptually'],
          ['consist', 'consistency', 'consist', 'consistent, consistently'],
          ['context', 'context', 'contextualise', 'contextual'],
          ['define', 'definition', 'define', 'definite, definitely'],
          ['environment', 'environment, environmentalist', '—', 'environmental, environmentally'],
          ['establish', 'establishment', 'establish', 'established'],
        ],
      },
    },
    {
      title: 'Common Collocations',
      content: '<p>Academic words are best learned as collocations — natural word partnerships that sound fluent to examiners.</p>',
      points: [
        'Collocations improve your Lexical Resource score in Writing',
        'Memorise 2-3 collocations per target word for maximum impact',
      ],
      examples: [
        { label: 'analysis', text: 'carry out an analysis, detailed analysis, statistical analysis, critical analysis' },
        { label: 'approach', text: 'adopt an approach, a holistic approach, a different approach to' },
        { label: 'assessment', text: 'make an assessment, risk assessment, continuous assessment' },
        { label: 'benefit', text: 'derive a benefit, mutual benefit, of great benefit to' },
        { label: 'concept', text: 'grasp a concept, key concept, abstract concept, the concept of' },
        { label: 'consistent', text: 'consistent with, remain consistent, consistent results' },
        { label: 'context', text: 'in the context of, social context, historical context, taken out of context' },
        { label: 'data', text: 'collect data, analyse data, empirical data, raw data' },
        { label: 'environment', text: 'natural environment, learning environment, protect the environment' },
        { label: 'established', text: 'well-established, established fact, firmly established' },
      ],
    },
    {
      title: 'Using AWL Words in IELTS Writing',
      content: '<p>Band 7+ essays demonstrate a range of academic vocabulary used naturally. Here is how to integrate Sublist 1 words into Task 2.</p>',
      points: [
        'Replace informal words with AWL equivalents (e.g. "good thing" → "benefit")',
        'Use collocations rather than isolated words',
        'Vary word forms to show grammatical range',
      ],
      examples: [
        { label: 'Weak sentence', text: 'Looking at the numbers shows some important things.' },
        { label: 'Strong sentence', text: 'A detailed analysis of the data reveals several significant trends.' },
        { label: 'Weak sentence', text: 'The way the school tests students is not good enough.' },
        { label: 'Strong sentence', text: 'The current approach to student assessment fails to capture individual progress.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Word to Definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        { text: 'What does "analysis" mean?', options: ['A detailed examination of something', 'A quick summary of a topic', 'A type of mathematical equation'], answer: 'A detailed examination of something', explanation: '"Analysis" comes from Greek and means breaking something into parts to understand it.' },
        { text: 'What does "consistent" mean?', options: ['Happening only once', 'Unchanging over time; in agreement', 'Extremely variable'], answer: 'Unchanging over time; in agreement', explanation: '"Consistent" describes something that remains the same or does not contradict.' },
        { text: 'What does "concept" mean?', options: ['A physical object', 'An abstract idea or general notion', 'A type of experiment'], answer: 'An abstract idea or general notion', explanation: 'A "concept" is an idea, often theoretical, such as "the concept of democracy".' },
        { text: 'What does "context" mean?', options: ['The circumstances that form the setting for something', 'A list of facts', 'A type of written text'], answer: 'The circumstances that form the setting for something', explanation: '"Context" refers to the situation or background that helps explain something.' },
        { text: 'What does "established" mean?', options: ['Recently created', 'Recognised and set up on a permanent basis', 'Temporary and informal'], answer: 'Recognised and set up on a permanent basis', explanation: '"Established" means something that has existed for a long time and is accepted.' },
      ],
    },
    {
      title: 'Complete the Sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct word from Sublist 1.',
      questions: [
        { text: 'The government adopted a new ___ to reducing pollution in urban areas.', answer: 'approach', explanation: '"Adopt an approach" is a common collocation meaning to choose a method.' },
        { text: 'The ___ collected from the survey will be analysed next week.', answer: 'data', explanation: '"Data" refers to facts and statistics gathered for analysis.' },
        { text: 'Protecting the natural ___ should be a priority for all nations.', answer: 'environment', explanation: '"Natural environment" is a frequent collocation in academic writing.' },
        { text: 'The main ___ of regular exercise is improved physical and mental health.', answer: 'benefit', explanation: '"Main benefit" is a common collocation used to introduce advantages.' },
      ],
    },
    {
      title: 'Choose the Correct Word Form',
      type: 'select',
      instructions: 'Select the grammatically correct form of the word for each sentence.',
      questions: [
        { text: 'The results were ___ across all groups. (consist)', options: ['consistency', 'consistent', 'consistently'], answer: 'consistent', explanation: 'An adjective is needed after "were" to describe the results.' },
        { text: 'She provided a thorough ___ of the poem. (analyse)', options: ['analytical', 'analysis', 'analyse'], answer: 'analysis', explanation: 'A noun is needed after "a thorough" — "analysis" is the noun form.' },
        { text: 'The charity is ___ to disadvantaged communities. (benefit)', options: ['beneficial', 'benefit', 'beneficiary'], answer: 'beneficial', explanation: 'An adjective is needed after "is" to describe the charity\'s impact.' },
        { text: 'We need a clear ___ of the term before proceeding. (define)', options: ['definite', 'definition', 'define'], answer: 'definition', explanation: '"A clear definition" requires the noun form.' },
      ],
    },
    {
      title: 'Use Words in Sentences',
      type: 'writing',
      instructions: 'Write a sentence using the given word appropriately in an academic context.',
      questions: [
        { text: 'Write a sentence using "assessment" in the context of education.', example: 'Regular assessment of student performance helps teachers identify areas for improvement.', tip: 'Use a collocation such as "continuous assessment" or "make an assessment".' },
        { text: 'Write a sentence using "environment" in the context of climate change.', example: 'Human activities have had a devastating impact on the natural environment over the past century.', tip: 'Pair "environment" with an adjective like "natural", "urban", or "learning".' },
        { text: 'Write a sentence using "approach" in the context of problem-solving.', example: 'A holistic approach to urban planning takes both economic and social factors into account.', tip: 'Try "adopt an approach" or "a new approach to" for natural-sounding academic English.' },
      ],
    },
  ],
  tips: [
    'Learn each word with 2-3 collocations, not just a dictionary definition',
    'Read academic articles and highlight Sublist 1 words to see them in natural contexts',
    'Keep a vocabulary journal organised by word family (noun, verb, adjective forms)',
    'Use spaced repetition: review new words after 1 day, 3 days, 1 week, and 1 month',
  ],
  dailyPractice: [
    'Choose 3 words and write an IELTS-style paragraph using all of them',
    'Read a BBC News article and identify any Sublist 1 words you find',
    'Quiz yourself: cover the definitions column and try to recall each meaning',
    'Practise converting sentences from informal to academic using AWL words',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublist 1 vocabulary course covering 12 high-frequency academic words: analysis, approach, area, assessment, benefit, concept, consistent, context, data, definition, environment, established. Includes word meanings, word families, collocations, and IELTS Writing integration strategies.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist1Data,
      title: awlSublist1Data.title + ' (Foundations)',
      overview: { ...awlSublist1Data.overview, duration: '15-20 minutes' },
      sections: awlSublist1Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist1Data.sections.length / 2))),
      exercises: awlSublist1Data.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist1Data,
      title: awlSublist1Data.title + ' (Advanced)',
      overview: { ...awlSublist1Data.overview, duration: '30-35 minutes' },
      sections: awlSublist1Data.sections,
      exercises: awlSublist1Data.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist1Data.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return awlSublist1Data;
}
